import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import relateApi from './relate.api';
import type { DbmsInfo } from './relate.types';

export interface Relate {
  apiToken?: string;
  clientId?: string;
  protocol?: string;
  url?: string;
}

export interface DesktopState {
  relate: Relate;
  desktopContext: boolean;
  cloningDesktopV1Dbmss: DbmsInfo[];
}

const initialState: DesktopState = {
  relate: {
    apiToken: undefined,
    clientId: undefined,
    protocol: undefined,
    url: undefined,
  },
  cloningDesktopV1Dbmss: [],
  desktopContext: false,
};

export type PreloadedStateData = Partial<DesktopState>;

export function createInitialState(data?: PreloadedStateData, state: DesktopState = initialState): DesktopState {
  const preloadedState = {
    ...state,
    // HACK: Deep property must be defined explicitly to be writeable
    relate: { ...state.relate },
  };

  if (data?.desktopContext !== undefined) {
    preloadedState.desktopContext = data.desktopContext;
  }

  if (data?.relate !== undefined) {
    preloadedState.relate = data.relate;
  }

  return preloadedState;
}

export const DESKTOP_PERSISTED_KEYS: (keyof DesktopState)[] = ['relate'];

export const slice = createSlice({
  name: 'desktop',
  initialState,
  reducers: {
    overrideInitialState(state: DesktopState, action: PayloadAction<PreloadedStateData | undefined>) {
      return createInitialState(action.payload, state);
    },

    setRelateTokens: (state: DesktopState, action: PayloadAction<Relate>) => {
      if (
        action.payload.apiToken !== undefined &&
        action.payload.clientId !== undefined &&
        action.payload.protocol !== undefined &&
        action.payload.url !== undefined
      ) {
        state.relate = { ...action.payload };
      }
    },
    setDesktopContext: (state: DesktopState, action: PayloadAction<boolean>) => {
      state.desktopContext = action.payload;
    },
  },

  extraReducers(builder) {
    builder.addMatcher(relateApi.endpoints.cloneDesktopV1Dbmss.matchPending, (state, action) => {
      const dbmss = action.meta.arg.originalArgs;
      state.cloningDesktopV1Dbmss = state.cloningDesktopV1Dbmss.concat(dbmss);
    });

    builder.addMatcher(relateApi.endpoints.cloneDesktopV1Dbmss.matchRejected, (state, action) => {
      const dbmss = action.meta.arg.originalArgs;
      state.cloningDesktopV1Dbmss = state.cloningDesktopV1Dbmss.filter((dbms) => dbmss.includes(dbms));
    });

    builder.addMatcher(relateApi.endpoints.cloneDesktopV1Dbmss.matchFulfilled, (state, action) => {
      const dbmss = action.meta.arg.originalArgs;
      state.cloningDesktopV1Dbmss = state.cloningDesktopV1Dbmss.filter((dbms) => dbmss.includes(dbms));
    });
  },
});

export const { setRelateTokens, setDesktopContext, overrideInitialState } = slice.actions;

export const { reducer, reducerPath } = slice;
